import React from 'react';
import { Box, Divider, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Dialog } from '../../../../components/others/Dialog';
import { ShowDocument } from '../../../../components/sharedViews/loadActions/ShowDocument'; // ajustar ruta real
import { DownloadFile } from '../../../../components/others/DownloadFile';
import { buildDocumentItem } from './buildDocumentItem';
import { useTranslation } from 'react-i18next';

/**
 * DialogSupportResponse
 *
 * Dialog informativo con campos dinámicos (fields) + anexos reales.
 *
 * Props:
 *  - open    {boolean}
 *  - onClose {fn}
 *  - fields  {Array<{ key, label, value }>}
 *  - anexos  {string[]}
 *  - dto     {object}  contexto para ShowDocument (resolveConfig, etc.)
 */
export const DialogSupportResponse = ({
    open,
    onClose,
    fields = [],
    anexos = [],
    dto,
}) => {
    const { t } = useTranslation();

    const cleanUrl = (url) => {
        if (!url) return url;

        // Elimina el dominio duplicado
        const domain = "https://ia-automation-storage-staging.s3.us-east-1.amazonaws.com/";
        if (url.startsWith(domain + "https://")) {
            url = url.replace(domain, "");
        }

        // Elimina un segundo bloque de parámetros (?X-Amz-...) si existe
        const secondQuestion = url.indexOf("?", url.indexOf("?") + 1);
        if (secondQuestion !== -1) {
            url = url.substring(0, secondQuestion);
        }

        // Elimina una barra invertida al final (\) si existe
        url = url.replace(/\\$/, "");

        return url;
    };
    const previewExtensions = [
        'pdf',
        'png',
        'jpg',
        'jpeg',
        'gif',
        'svg',
        'webp'
    ];

    const canPreviewFile = (url) => {
        if (!url) return false;

        const fileName = url.split('/').pop()?.split('?')[0];
        const extension = fileName?.split('.').pop()?.toLowerCase();

        return previewExtensions.includes(extension);
    };

    const getRelativePath = (cleanPath) => {
        if (!cleanPath) return "";

        const url = new URL(cleanPath);
        return url.pathname.replace(/^\/+/, "");
    };

    return (
        <Dialog
            openDialog={open}
            widthDialog="sm"
            styleDialog={{ width: '480px', backgroundColor: '#161717', borderRadius: 2 }}
            title={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontWeight: 'bold', fontSize: 17, color: '#f9f9f9' }}>
                        {t('support.yourResponse', 'Respuesta')}
                    </Typography>
                    <IconButton size="small" onClick={onClose}>
                        <CloseIcon fontSize="small" sx={{ color: "#f9f9f9" }} />
                    </IconButton>
                </Box>
            }
            styleTitle={{ px: 3, pt: 3, pb: 2 }}
            content={
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography sx={{ fontWeight: 'bold', fontSize: 13, letterSpacing: 0.5, color: '#f9f9f9' }}>
                        {t('support.support', 'Información')}
                    </Typography>

                    <Divider />

                    {/* ── Campos dinámicos ── */}
                    {fields.map((field, idx) => (
                        <React.Fragment key={field.key}>
                            <Box>
                                <Typography sx={{ fontSize: 12, color: '#f9f9f9' }}>
                                    {field.label}
                                </Typography>
                                <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#f9f9f9' }}>
                                    {field.value || '-'}
                                </Typography>
                            </Box>
                            {idx < fields.length - 1 && <Divider />}
                        </React.Fragment>
                    ))}

                    {/* ── Anexos ── */}
                    {anexos.length > 0 && (
                        <>
                            <Divider />
                            <Box>
                                <Typography sx={{ fontSize: 12, color: '#f9f9f9', mb: 1 }}>
                                    {t('support.attachment', 'Anexo')}
                                </Typography>
                                <Stack spacing={0.5}>
                                    {anexos.map((path, idx) => {
                                        const cleanPath = cleanUrl(path);
                                        console.log('DialogSupportResponse - anexos path:', cleanPath);

                                        if (!cleanPath) return null;
                                        
                                        const relativePath = getRelativePath(cleanPath);
                                        console.log('DialogSupportResponse - relativePath:', relativePath);

                                        const item = buildDocumentItem(relativePath);

                                        const isPreview = canPreviewFile(relativePath);

                                        const fileName = relativePath.split('/').pop();

                                        return (
                                            <Box
                                                key={`${cleanPath}-${idx}`}
                                                display="flex"
                                                alignItems="center"
                                                sx={{ position: 'relative', maxWidth: 300 }}
                                            >
                                                {isPreview ? (
                                                    <ShowDocument
                                                        data={item}
                                                        dto={dto}
                                                    />
                                                ) : (
                                                    <DownloadFile
                                                        path={relativePath}
                                                        bucket="ia"
                                                        idFlow="900114098-API-DOWNLOAD-BASE64"
                                                    />
                                                )}

                                                <Typography
                                                    sx={{
                                                        fontSize: 14,
                                                        fontWeight: 600,
                                                        color: '#f9f9f9'
                                                    }}
                                                >
                                                    {fileName}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            </Box>
                        </>
                    )}
                </Box>
            }
            styleContent={{ px: 3, pb: 3 }}
        />
    );
};